"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, X } from "lucide-react";
import { PublicAuthNav } from "./PublicAuthNav";
import { Button } from "@/components/ui/button";
import { CONTACT_EMAIL } from "@/lib/constants";
import { cn } from "@/lib/utils";

/**
 * Public header navigation. A client component for one reason only: the
 * mobile drawer needs open/closed state. It carries no server data — same
 * split as PortalFrame does for the portal chrome.
 *
 * The link set grew from one ("Directory") to five plus a CTA on founder
 * feedback (2026-08-04). At that width a plain flex row overflows a phone,
 * which is how the portals broke in July — so below `md` this collapses to
 * a drawer instead of wrapping.
 *
 * PublicAuthNav stays a separate leaf: see that file for why the signed-in
 * swap has to resolve client-side rather than with Clerk's server `Show`.
 */

const LINKS = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  { href: "/services", label: "Our Services" },
  { href: "/directory", label: "Directory" },
  { href: "/team", label: "Team" },
] as const;

const linkClass =
  "text-foreground/80 transition-colors duration-150 hover:text-foreground";

export function PublicNav() {
  const [open, setOpen] = useState(false);
  const pathname = usePathname();

  // Navigating from a drawer link must not leave the drawer covering the page.
  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <nav className="hidden items-center gap-6 text-sm font-medium md:flex">
        {LINKS.map((link) => (
          <Link key={link.href} href={link.href} className={linkClass}>
            {link.label}
          </Link>
        ))}
        <PublicAuthNav />
        <Button asChild size="sm">
          <a href={`mailto:${CONTACT_EMAIL}`}>Let&apos;s talk</a>
        </Button>
      </nav>

      <button
        type="button"
        aria-label="Open menu"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="rounded-md p-2 text-foreground hover:bg-muted md:hidden"
      >
        <Menu className="size-5" />
      </button>

      {open ? (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
        />
      ) : null}

      <div
        className={cn(
          "fixed inset-y-0 right-0 z-40 flex w-64 flex-col gap-6 border-l border-border bg-background p-6 transition-transform duration-200 md:hidden",
          open ? "translate-x-0" : "translate-x-full"
        )}
      >
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className="self-end rounded-md p-1 text-foreground/70 hover:bg-muted"
        >
          <X className="size-5" />
        </button>

        <nav className="flex flex-col gap-4 text-sm font-medium">
          {LINKS.map((link) => (
            <Link key={link.href} href={link.href} className={linkClass}>
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex flex-col gap-4 border-t border-border pt-4 text-sm font-medium">
          <PublicAuthNav />
        </div>

        <Button asChild className="mt-auto">
          <a href={`mailto:${CONTACT_EMAIL}`}>Let&apos;s talk</a>
        </Button>
      </div>
    </>
  );
}
